import { CalendarDays, AlertTriangle, NotebookPen, Clock } from 'lucide-react';
import { InputInfo } from '@/components/ui';
import { DatePicker } from '@/components/ui/DatePicker';
import { UNAVAILABILITY_TYPE_TRANSLATION } from '@/constants/professional/translations';
import { useEffect, useState } from 'react';
import { cn } from '@/lib/utils';

interface CreateUnavailabityFormProps {
	onAction: (data: ProfessionalUnavailabilitys.Create) => void;
	isSuccess?: boolean;
	defaultData?: ProfessionalUnavailabilitys.ProfessionalUnavailability;
	onCancel: () => void;
}

const CreateUnavailabityForm = ({
	onAction,
	isSuccess,
	defaultData,
	onCancel,
}: CreateUnavailabityFormProps) => {
	const [selectedInitalTime, setSelectedInitialTime] = useState<
		string | undefined
	>(defaultData?.start_date);
	const [selectedFinalTime, setSelectedFinalTime] = useState<
		string | undefined
	>(defaultData?.end_date);
	const [selectedType, setSelectedType] = useState<
		ProfessionalUnavailabilitys.UnavailabilityType | undefined
	>(defaultData?.type);
	const [reason, setReason] = useState(defaultData?.reason || '');

	useEffect(() => {
		if (isSuccess) {
			setSelectedInitialTime(undefined);
			setSelectedFinalTime(undefined);
			setReason('');
			setSelectedType(undefined);
		}
	}, [isSuccess]);

	const adjustTimezone = (isoString?: string) => {
		if (!isoString) return null;
		const date = new Date(isoString);
		date.setMinutes(date.getMinutes() + date.getTimezoneOffset());
		return date;
	};

	const isValid = selectedInitalTime && selectedFinalTime && selectedType;

	return (
		<div className="w-full space-y-8 rounded-3xl border border-stone-200 bg-white p-6 shadow-sm md:p-8">
			{/* 1. Tipo de Indisponibilidade */}
			<div className="space-y-3">
				<div className="flex items-center gap-2 font-bold text-stone-900">
					<CalendarDays className="size-4" />
					<span>Tipo de indisponibilidade</span>
				</div>
				<div className="flex flex-wrap gap-2">
					{Object.entries(UNAVAILABILITY_TYPE_TRANSLATION).map(
						([key, label]) => (
							<button
								key={key}
								type="button"
								onClick={() =>
									setSelectedType(
										key as ProfessionalUnavailabilitys.UnavailabilityType,
									)
								}
								className={cn(
									'whitespace-nowrap rounded-xl border px-5 py-2 text-sm font-semibold transition-all',
									selectedType === key
										? 'border-stone-950 bg-stone-950 text-white shadow-md'
										: 'border-stone-200 bg-white text-stone-500 hover:border-stone-400 hover:bg-stone-50',
								)}
							>
								{label}
							</button>
						),
					)}
				</div>
			</div>

			{/* 2. Datas */}
			<div className="flex w-full flex-col gap-4 md:flex-row">
				<div className="w-full space-y-3">
					<div className="flex items-center gap-2 font-bold text-stone-900">
						<Clock className="size-4" />
						<span>Data inicial</span>
					</div>
					<div className="rounded-xl border border-transparent bg-stone-50 transition-colors focus-within:ring-2 focus-within:ring-stone-950/10 hover:bg-stone-100">
						<DatePicker
							defaultValue={
								defaultData?.start_date
									? adjustTimezone(defaultData.start_date)
									: null
							}
							onChange={(date: Date | null) =>
								setSelectedInitialTime(
									date ? date.toISOString() : undefined,
								)
							}
						/>
					</div>
				</div>

				<div className="w-full space-y-3">
					<div className="flex items-center gap-2 font-bold text-stone-900">
						<Clock className="size-4" />
						<span>Data Final</span>
					</div>
					<div className="rounded-xl border border-transparent bg-stone-50 transition-colors focus-within:ring-2 focus-within:ring-stone-950/10 hover:bg-stone-100">
						<DatePicker
							defaultValue={
								defaultData?.end_date
									? adjustTimezone(defaultData.end_date)
									: null
							}
							onChange={(date: Date | null) =>
								setSelectedFinalTime(
									date ? date.toISOString() : undefined,
								)
							}
						/>
					</div>
				</div>
			</div>

			{/* 3. Motivo */}
			<div className="w-full space-y-3">
				<div className="flex items-center gap-2 font-bold text-stone-900">
					<NotebookPen className="size-4" />
					<span>Motivo</span>
				</div>
				<div>
					<textarea
						value={reason}
						onChange={(e) => setReason(e.target.value)}
						placeholder="Ex: Consultas médicas, viagem de família..."
						className="h-32 w-full resize-none rounded-2xl border-none bg-stone-50 p-4 font-medium text-stone-700 outline-none transition-all placeholder:text-stone-300 focus:ring-2 focus:ring-stone-950/10"
						maxLength={255}
					/>
					{/* Removido o absolute para não cobrir os botões abaixo */}
					<div className="mt-1 flex justify-end">
						<InputInfo text="Máximo de 255 caracteres (Opcional)" />
					</div>
				</div>
			</div>

			{/* 4. Aviso Laranja */}
			<div className="flex items-center gap-3 rounded-2xl border border-orange-100 bg-orange-50 p-4">
				<div className="shrink-0 rounded-lg bg-white p-1.5 shadow-sm">
					<AlertTriangle className="size-5 text-orange-600" />
				</div>
				<p className="text-sm font-medium leading-tight text-orange-800">
					Durante este período, você ficará bloqueado para novos
					agendamentos em sua agenda.
				</p>
			</div>

			{/* 5. Botões de Ação - IDENTICOS AO SEU EXEMPLO */}
			<div className="flex w-full gap-4 pt-4">
				<button
					type="button"
					onClick={onCancel}
					className="flex-1 rounded-2xl border border-stone-200 px-6 py-4 text-center font-bold text-stone-600 transition-colors hover:bg-stone-50"
				>
					Voltar
				</button>

				<button
					type="button"
					disabled={!isValid}
					onClick={() =>
						onAction({
							end_date: selectedFinalTime!,
							start_date: selectedInitalTime!,
							type: selectedType!,
							reason,
						})
					}
					className={cn(
						'flex-[2] transform rounded-2xl px-6 py-4 text-center font-bold text-white shadow-lg transition-all active:scale-[0.98]',
						!isValid
							? 'cursor-not-allowed bg-stone-300 shadow-none'
							: 'bg-stone-950 shadow-stone-200 hover:bg-stone-900',
					)}
				>
					{defaultData
						? 'Confirmar alterações'
						: 'Salvar indisponibilidade'}
				</button>
			</div>
		</div>
	);
};

export { CreateUnavailabityForm };
